import { useMemo } from 'react';
import { useStorage } from '@funtech-int/web-toolkit';
import { useFeatureFlags } from '@funtech-int/yango-shared';

import { ConfigWithSourceCallback, Source } from '@/scenarios/legacy-player/types/config';
import { clientConfig } from '@/config/client';

import { getPreferredUserVideoQuality } from '../../utils/preferred-user-video-quality';

import { usePlayerTestIds } from './use-player-test-ids';
import { useYaPlayerVersion } from './use-ya-player-version';
import { useYaspVersion } from './use-yasp-version';

interface Props {
    getSource: (props: { contentId: string; vsid: string }) => Promise<Source | Source[]>;
}

const {
    serviceId,
    productServiceName,
    playerTelemetryUrl,
    playerPerfUrl,
    filmoscopeBaseStaticPath,
    filmoscopeYaspScriptUrl,
} = clientConfig;

export const useBaseConfig: (props: Props) => ConfigWithSourceCallback = ({ getSource }) => {
    const { flags } = useFeatureFlags();

    const isPlayerDebugMode = flags.get('isPlayerDebugMode', 'boolean', false);
    const filmoscopeVersion = flags.get('filmoscopeVersion', 'string', '');
    const isPlayerSDKEnabled = flags.get('isPlayerSDKEnabled', 'boolean', false);

    const testIds = usePlayerTestIds();
    const yaPlayerVersion = useYaPlayerVersion();
    const yaspVersion = useYaspVersion();
    const storage = useStorage();

    return useMemo(
        () => ({
            configType: 'sourceCallback',
            getSource: (params) => getSource({ ...params }),
            testIds,
            debug: isPlayerDebugMode,
            autoplay: 'Allowed',
            serviceId,
            yaspVersion,
            filmoscopeVersion,
            yaPlayerVersion,
            maxABRWidth: getPreferredUserVideoQuality(storage),
            from: productServiceName,
            playerTelemetryUrl,
            filmoscopeBaseStaticPath,
            filmoscopeYaspScriptUrl,
            filmoscopeDisableYaspLoader: Boolean(filmoscopeYaspScriptUrl),
            playerPerfUrl,
            featureFlags: {
                useSDK: isPlayerSDKEnabled,
            },
        }),
        [
            filmoscopeVersion,
            getSource,
            isPlayerDebugMode,
            isPlayerSDKEnabled,
            testIds,
            yaPlayerVersion,
            yaspVersion,
            storage,
        ]
    );
};
